import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Folder, Plus, Loader, AlertCircle, Calendar, FileText, GitBranch } from 'lucide-react';
import { API } from '../App';
import Header from '../components/layout/Header';

/**
 * Projects list page (#1101).
 * Surfaces backend GET /api/projects (#1093). Lets the user create new
 * projects without admin/MCP. Click a project → /projects/{id}.
 */
export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await API.get('/api/projects');
      setProjects(r.data?.projects || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load projects');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await API.post('/api/projects', {
        name: newName.trim(),
        description: newDescription.trim() || undefined,
      });
      setShowCreate(false);
      setNewName('');
      setNewDescription('');
      navigate(`/projects/${r.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create project');
    }
    setCreating(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F1E8]">
      <Header />
      <div className="max-w-6xl mx-auto px-6 pt-20 pb-10">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1
              className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8] flex items-center gap-3"
              data-testid="projects-page-title"
            >
              <Folder className="w-7 h-7 text-[#F2A900]" />
              Projects
            </h1>
            <p className="text-sm text-[#A0A0A0] mt-2 max-w-2xl">
              A project owns N versioned frays plus shared reference contexts that auto-flow into every fray inside.
              Use projects to iterate (v1 → v2 → v3) without re-uploading prior content.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-project-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#FF4D00] hover:bg-[#FF6A2A] text-[#0A0A0A] font-mono text-xs uppercase tracking-wider transition-colors rounded"
          >
            <Plus className="w-4 h-4" />
            New Project
          </button>
        </div>

        {showCreate && (
          <div
            className="mb-8 p-5 bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg"
            data-testid="new-project-form"
          >
            <h3 className="text-xs font-mono uppercase tracking-wider text-[#F2A900] mb-4">
              Create Project
            </h3>
            <label className="block text-[10px] font-mono text-[#A0A0A0] mb-1">
              NAME
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. PSA Skincare Pitch Deck Reviews"
              data-testid="new-project-name"
              className="w-full mb-3 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm text-[#F5F1E8] focus:border-[#FF4D00] focus:outline-none"
            />
            <label className="block text-[10px] font-mono text-[#A0A0A0] mb-1">
              DESCRIPTION (OPTIONAL)
            </label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Short context for what this project is iterating on"
              data-testid="new-project-description"
              rows={3}
              className="w-full mb-4 px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm text-[#F5F1E8] focus:border-[#FF4D00] focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreate(false);
                  setNewName('');
                  setNewDescription('');
                }}
                className="px-3 py-1.5 text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                data-testid="create-project-submit"
                className="px-4 py-1.5 bg-[#FF4D00] hover:bg-[#FF6A2A] disabled:bg-[#2A2A2A] disabled:text-[#A0A0A0] text-[#0A0A0A] font-mono text-xs uppercase rounded transition-colors"
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-[#A0A0A0] font-mono text-sm flex items-center justify-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            Loading projects…
          </div>
        )}

        {error && (
          <div className="p-4 bg-[#A61E17]/20 border border-[#A61E17]/40 rounded-lg flex items-center gap-3 text-sm">
            <AlertCircle className="w-5 h-5 text-[#A61E17]" />
            <span className="text-[#F5F1E8]">{error}</span>
          </div>
        )}

        {!loading && !error && projects.length === 0 && (
          <div
            className="text-center py-16 text-[#A0A0A0] font-mono text-sm border border-dashed border-[#2A2A2A] rounded-lg"
            data-testid="projects-empty-state"
          >
            <Folder className="w-10 h-10 mx-auto mb-3 text-[#2A2A2A]" />
            No projects yet. Click "New Project" to create one.
          </div>
        )}

        {!loading && !error && projects.length > 0 && (
          <ul className="space-y-2" data-testid="projects-list">
            {projects.map((p) => (
              <li
                key={p.id}
                onClick={() => navigate(`/projects/${p.id}`)}
                data-testid={`project-row-${p.id}`}
                className="p-4 bg-[#1E1E1E] border border-[#2A2A2A] hover:border-[#FF4D00] rounded-lg cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8] truncate">
                      {p.name}
                    </h3>
                    {p.description && (
                      <p className="text-xs text-[#A0A0A0] mt-1 line-clamp-2">
                        {p.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-[10px] font-mono text-[#A0A0A0] shrink-0">
                    {p.fray_count != null && (
                      <span className="flex items-center gap-1">
                        <GitBranch className="w-3 h-3" />
                        {p.fray_count} {p.fray_count === 1 ? 'fray' : 'frays'}
                      </span>
                    )}
                    {p.context_count != null && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {p.context_count}{' '}
                        {p.context_count === 1 ? 'context' : 'contexts'}
                      </span>
                    )}
                    {p.updated_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(p.updated_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
